import type { CSSProperties, JSX, ReactNode } from 'react'
import logoUrl from '../assets/aps-logo.svg'
import type { Role, User } from '../api.js'
import { T } from '../tokens.js'

/**
 * The app chrome: the 5px brand rainbow rule at the very top of every screen,
 * the app bar with the APS mark at 96px and the `Kilowatt Estimator` wordmark,
 * the four section tabs, and the signed-in rep with a way out.
 *
 * Transcribed from the mock's `isList` branch, with two deliberate changes.
 *
 * **There is a fifth tab the mock does not have.** `Notes` is the cross-book
 * note search (R-29, spec §7.7). It is a section rather than a control on the
 * targeting list because it is scoped to neither the rail's filters nor to one
 * account, and because a screen no URL and no navigation reaches is a screen
 * nobody uses.
 *
 * **One of the five tabs leads nowhere, and it says so.** Accuracy is in no
 * plan on this campaign at all. The mock draws all four as plain `<span>`s with
 * no handler, which is indistinguishable — to a rep, on a Tuesday — from a tool
 * whose navigation is broken. It is rendered `disabled` here, with a `title`
 * that says it is not built yet, so the tool reads as unfinished-and-honest
 * rather than as broken. It is the same defect as the sign-in screen's inert
 * "keep me signed in" checkbox, which Task 10 dropped for the same reason.
 *
 * **Two tabs are built and neither is a link for everybody.** Every route the
 * configuration console opens on is `requireRole('program_manager')`, and
 * everything under `/api/characterizations` — the queue, the impact preview and
 * the decision — is `requireRole('analyst')`. A rep who clicked either would
 * land on a screen whose first request is a 403: a dead end reached from the
 * app bar, which is worse than the disabled tab it replaced. `ListsPanel`
 * already draws this line for the same reason and says it in the same words:
 * firing a request every rep's session answers 403 to, to learn a fact the
 * session already carries, is the mistake. So each is a real link for the one
 * role that owns it and a disabled control with the **actual** reason for
 * everyone else — never "not built yet", which for these two sections is no
 * longer true and would be a second lie replacing the first.
 *
 * **The two roles do not overlap, and that is the point of asking twice.** A
 * program manager is not an analyst; a `role !== 'rep'` shortcut would open the
 * review queue to the program team and the console to the analysts, and both
 * would be 403s reached from the bar. Each tab names its own role.
 *
 * **Why the `title` is on the wrapper as well as the button.** Browsers do not
 * dispatch mouse events to a disabled form control, so a `title` on a disabled
 * `<button>` never opens a tooltip in Chrome or Firefox — the one place the
 * explanation is actually read. The wrapping `<span>` is what the pointer
 * hovers, so it carries the same text; the button keeps its own copy because
 * that is what reaches the accessibility tree, where a disabled control is
 * still announced.
 *
 * **The scenario treatment is in the layout, not in a corner.** A what-if adds
 * a 6px red bar *above* the brand rule and the scenario's name beside the
 * wordmark, so the top of the window says what it is showing before a rep has
 * read a single figure. See `scenario` below.
 */

export type Section = 'targeting' | 'notes' | 'review-queue' | 'program-config'

interface Tab {
  id: string
  label: string
  /** Where the section lives. Only read for an open one; a closed tab is a disabled button, not a link. */
  href: string
  /**
   * `null` on a section this signed-in user can open. Otherwise the reason they
   * cannot — which is **not** always "it does not exist yet". A section can be
   * shut to somebody because it was never built, or because it is not theirs,
   * and a tooltip that gives the first reason for the second case sends them to
   * wait for a release that already shipped.
   */
  unbuilt: string | null
}

/**
 * Why `Program config` is shut to a rep, an analyst and ops.
 *
 * Not a hedge and not a security control — the guard is `requireRole` on the
 * server and this changes nothing about it. It is the difference between a
 * navigation that tells you where you cannot go and one that walks you into a
 * 403.
 */
export const PROGRAM_TEAM_ONLY
  = 'Program configuration is managed by the program team — your account cannot open it.'

/**
 * Why `Review queue` is shut to a rep, a program manager and ops.
 *
 * The same shape as {@link PROGRAM_TEAM_ONLY} and for the same reason, against
 * a different role: every route under `/api/characterizations` is
 * `requireRole('analyst')`. Filing research is a separate, `requireAuth` route
 * on the account — `proposeCharacterization` writes `state = 'proposed'`
 * unconditionally, so nobody can approve their own claim — but **no screen
 * calls it yet**, which is a gap recorded in Plan 6's Task 8 rather than
 * something this tooltip should describe as though it existed.
 */
export const ANALYST_ONLY
  = 'Characterization review is an analyst’s queue — your account cannot open it.'

/**
 * The tabs as this signed-in user sees them.
 *
 * A function rather than a constant because one of the five depends on who is
 * looking. Everything else about the bar is the same for everybody, and the
 * order never changes: a nav that reorders itself by role is a nav nobody can
 * be told about over the phone.
 */
function tabsFor(role: Role): readonly Tab[] {
  return [
    { id: 'targeting', label: 'Targeting', href: '/', unbuilt: null },
    // Notes across the book (R-29, spec §7.7). It is a tab rather than a control
    // inside the targeting list because it is not scoped to the list's filters or
    // to any one account: it searches every note on the book, and a search that
    // ignores the rail would be misleading if it were drawn beside it.
    { id: 'notes', label: 'Notes', href: '/notes', unbuilt: null },
    // The analyst's characterization review queue (R-24, R-25, Plan 6 Task 8).
    // A link for an analyst and a disabled control with the true reason for
    // everyone else — see the module docblock, and note that this asks for
    // `analyst` rather than "not a rep": a program manager reaching this screen
    // gets a 403 on its very first request.
    {
      id: 'review-queue',
      label: 'Review queue',
      href: '/review',
      unbuilt: role === 'analyst' ? null : ANALYST_ONLY,
    },
    {
      id: 'program-config',
      label: 'Program config',
      href: '/config',
      unbuilt: role === 'program_manager' ? null : PROGRAM_TEAM_ONLY,
    },
    // Said separately and said straight: accuracy tracking is not scheduled
    // anywhere. A tab promising a release that is not planned is the same lie as
    // one pretending to be clickable.
    { id: 'accuracy', label: 'Accuracy', href: '/', unbuilt: 'Not built yet — accuracy tracking is not scheduled.' },
  ]
}

const tabBase: CSSProperties = {
  padding: '16px 15px',
  fontFamily: T.fontFamily,
  fontSize: 11,
  fontWeight: T.weight.semi,
  letterSpacing: '0.09em',
  textTransform: T.micro.transform,
  background: 'transparent',
  // Every tab carries the 3px rule so the active one does not sit 3px taller
  // than its neighbours. The mock draws this with an inset box-shadow; a
  // transparent border is the same picture without a shadow, and "borders do
  // the work shadows normally would" is this design system's stated rule.
  borderTop: 0,
  borderLeft: 0,
  borderRight: 0,
  borderBottom: '3px solid transparent',
  display: 'block',
}

/** A section that exists. The red rule is added on top of this for the current one. */
const linkTab: CSSProperties = {
  ...tabBase,
  color: T.color.ink,
  textDecoration: 'none',
}

const activeTab: CSSProperties = {
  ...linkTab,
  borderBottomColor: T.color.red,
}

const disabledTab: CSSProperties = {
  ...tabBase,
  color: T.color.micro,
  // `not-allowed` rather than `default`: the pointer is the only hint a mouse
  // user gets before the tooltip arrives.
  cursor: 'not-allowed',
}

/** The 6px red bar the mock draws **above** the brand rule under a scenario. */
export const SCENARIO_BAR_HEIGHT = 6

/**
 * The app bar's height in px, **border-box** — the 1.5px rule under it is
 * inside this number, because `index.html` sets `box-sizing: border-box` on
 * everything.
 *
 * It is pinned rather than left to the content for one reason: a screen that
 * scrolls a pane instead of the page has to know how much of the viewport the
 * chrome has already taken, and "whatever the tallest of a logo, a tab and a
 * button came out to in this font" is not a number any other module can be
 * given. 48 is the height the bar already had — the tabs are the tallest thing
 * in it at 16px of padding either side of an 11px line, ~46px — so this pins
 * what was there rather than changing it.
 *
 * **Change this and the two screens that subtract it follow automatically**:
 * `TargetingList` bounds its layout with it, and `AccountDetail` caps its
 * sticky rail with it. Neither writes 48.
 */
export const APP_BAR_HEIGHT = 48

/**
 * How much of the viewport the chrome occupies before a screen gets any: the
 * brand rule plus the app bar. **Not** the scenario bar, which is 6px more and
 * only present under a what-if — see `AccountDetail.tsx`'s rail cap for why
 * those 6px are left out rather than threaded through.
 */
export const CHROME_HEIGHT = T.rainbowHeight + APP_BAR_HEIGHT

export interface ChromeProps {
  user: User
  active: Section
  onSignOut: () => void
  /**
   * The active what-if's name, or `null`.
   *
   * R-18 and spec §7.4: a scenario is a state of the *page*, not a badge on one
   * panel of it. The two things that belong up here are the 6px red bar over
   * the brand rule and the scenario's name beside the wordmark — the parts a
   * rep cannot scroll past and cannot crop out of a screenshot of the top of
   * the window. The band, the revert control and the red figures are the
   * drill-down's, in `screens/AccountDetail.tsx`.
   */
  scenario?: string | null
  children?: ReactNode
}

export function Chrome({ user, active, onSignOut, scenario = null, children }: ChromeProps): JSX.Element {
  return (
    /*
      **The window is the box, and `<main>` is what scrolls.**

      This was `minHeight: '100vh'` with the document scrolling. A screen that
      wants to scroll one pane and hold another still — the targeting list's
      filter rail and sticky table header — cannot be built on top of that: it
      needs a height it can bound, and `min-height` gives a floor rather than a
      height, so a tall child grows the page and every `overflow` below it is
      inert. That is the usual cause of "I set overflow and nothing happened".

      Two things follow, and both are improvements rather than costs:

      - The app bar and the brand rule no longer scroll away. They are 53px the
        rep keeps.
      - `position: sticky` inside a screen now sticks to the top of `<main>`,
        which is directly under the app bar, instead of to the top of the window
        underneath it. `ScenarioBand` and `AsOfBand` in the drill-down land in
        the right place for the first time.

      `overflowY: 'auto'` on `<main>` is not optional. Without it a screen taller
      than the pane paints outside a box the document has stopped growing for,
      and the bottom of it is unreachable by any scroll.
    */
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.fontFamily,
        color: T.color.ink,
      }}
    >
      {scenario === null
        ? null
        : (
          <div
            data-testid="scenario-bar"
            style={{ height: SCENARIO_BAR_HEIGHT, background: T.color.red, flexShrink: 0 }}
          />
        )}
      <div
        data-testid="rainbow-rule"
        style={{ height: T.rainbowHeight, background: T.rainbow, flexShrink: 0 }}
      />
      <header
        style={{
          background: '#fff',
          borderBottom: `1.5px solid ${T.color.rule}`,
          // Pinned, and `flexShrink: 0` so the pane below cannot squeeze it —
          // a flex item's default is to shrink, and a bar that is 48px on the
          // list and 41px on a long drill-down is a bar that moves.
          height: APP_BAR_HEIGHT,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 28, paddingLeft: 24 }}>
          <img src={logoUrl} alt="APS" style={{ width: 96, height: 'auto', display: 'block' }} />
          <div style={{ fontSize: 13, fontWeight: T.weight.bold, letterSpacing: '0.02em' }}>
            Kilowatt Estimator
          </div>
          {scenario === null
            ? null
            : (
              <div
                data-testid="chrome-scenario"
                data-scenario-name={scenario}
                style={{
                  border: `1.5px solid ${T.color.red}`,
                  background: T.tint.red,
                  color: T.color.declinedInk,
                  borderRadius: T.radius.pill,
                  padding: '4px 12px',
                  fontSize: 11,
                  fontWeight: T.weight.bold,
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >
                {`Scenario — ${scenario}`}
              </div>
            )}
          <nav aria-label="Sections" style={{ display: 'flex' }}>
            {tabsFor(user.role).map((tab) => (tab.unbuilt === null
              ? (
                // A real link, not a `<span>`: it is tabbable, it is announced
                // as a link, and it opens in a new tab like every other link a
                // rep has ever used. `aria-current` is what says *this* one is
                // the page you are on — colour alone does not.
                <a
                  key={tab.id}
                  href={tab.href}
                  aria-current={tab.id === active ? 'page' : undefined}
                  // The red rule marks the section the rep is *on*. With two
                  // built sections it can no longer be a property of "is a
                  // link" — underlining both would say the rep is in two places.
                  style={tab.id === active ? activeTab : linkTab}
                >
                  {tab.label}
                </a>
              )
              : (
                <span key={tab.id} title={tab.unbuilt} style={{ display: 'flex' }}>
                  <button type="button" disabled title={tab.unbuilt} style={disabledTab}>
                    {tab.label}
                  </button>
                </span>
              )))}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingRight: 24, fontSize: 12 }}>
          <span style={{ color: T.color.muted }}>{user.displayName}</span>
          <button
            type="button"
            onClick={onSignOut}
            style={{
              border: `1.5px solid ${T.color.border}`,
              background: '#fff',
              color: T.color.ink,
              padding: '10px 14px',
              fontFamily: T.fontFamily,
              fontSize: T.micro.size,
              fontWeight: T.weight.bold,
              letterSpacing: '0.08em',
              textTransform: T.micro.transform,
              borderRadius: T.radius.button,
              cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      {/*
        `flex: 1` takes the rest of the window and `minHeight: 0` is what lets
        it be *shorter* than its content — without it a flex item refuses to go
        below its content height and the pane never scrolls at all.

        It stays a **block** container rather than becoming a second flex
        column, and that is deliberate. A flex column with `overflow` does not
        scroll its children; it *shrinks* them, because a flex item's default is
        `flex-shrink: 1` against a container with a definite height. Every
        screen but the targeting list is ordinary block flow that wants to be
        its natural height and scroll, and turning this into a flex container
        would squash the drill-down's chart, cards and note feed instead.
      */}
      <main style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</main>
    </div>
  )
}
